import { FilterInput } from '@kinetic-ui/data-layer';
import { generateId, useOrganizationWidePreference } from 'kineticUtils';
import { useUserTenantPreference } from 'kineticUtils/hooks/preferences/useUserTenantPreference';
import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';

import { UpdateConfig } from 'components/Table/contexts/ColumnsContext/types';
import { useCombinedTableControlsContext } from '../contexts/context';
import { useFiltersContext } from '../contexts/filtersContext';

export type SavedFilter = {
  id: string;
  name: string;
  filters: FilterInput[];
  columns: UpdateConfig[] | null;
  default: boolean;
};
type FilterPreference = {
  filters: Record<string, SavedFilter>;
  activeSavedFilterId?: string;
  showFilterChips: boolean;
};

type OrganizationFilterPreference = {
  filters: Record<string, SavedFilter>;
};

const newSavedFilter = ({
  name,
  filters,
  columns,
}: {
  name: string;
  filters: FilterInput[];
  columns: UpdateConfig[] | null;
}): SavedFilter => {
  return {
    id: generateId(),
    name,
    filters,
    columns,
    default: false,
  };
};

const EMPTY_PREFERENCE_VALUE: FilterPreference = { filters: {}, showFilterChips: true };
const EMPTY_ORG_PREFERENCE_VALUE: OrganizationFilterPreference = { filters: {} };
export const useSavedFilters = ({ preferenceKey }: { preferenceKey?: PREFERENCE_KEYS }) => {
  const { setPreference, value, loading } = useUserTenantPreference<FilterPreference>({
    key: preferenceKey,
  });

  const [preferenceColumns, setPreferenceColumns] = useState<UpdateConfig[]>();

  const { populateForm, toForm, form } = useFiltersContext();
  const { columnsRef } = useCombinedTableControlsContext();

  const {
    setPreference: setOrgPreference,
    value: orgPrefValue,
    loading: orgLoading,
  } = useOrganizationWidePreference<OrganizationFilterPreference>({
    key: preferenceKey,
  });

  const preferenceValue = useMemo(() => {
    if (isEqual(value, {})) {
      return EMPTY_PREFERENCE_VALUE;
    }
    return value ?? EMPTY_PREFERENCE_VALUE;
  }, [value]);

  const orgValue = useMemo(() => {
    if (isEqual(orgPrefValue, {})) {
      return EMPTY_ORG_PREFERENCE_VALUE;
    }
    return orgPrefValue ?? EMPTY_ORG_PREFERENCE_VALUE;
  }, [orgPrefValue]);

  const filterChipsHide = useCallback(() => {
    setPreference({ ...preferenceValue, showFilterChips: false });
  }, [preferenceValue, setPreference]);

  const filterChipsShow = useCallback(() => {
    setPreference({ ...preferenceValue, showFilterChips: true });
  }, [preferenceValue, setPreference]);

  const updateSavedFilter = useCallback(
    ({ id, filters }: { id: string; filters: FilterInput[] }) => {
      const updatedFilter = { ...preferenceValue.filters[id] };
      updatedFilter.filters = filters;
      const updatedFilters = {
        ...preferenceValue.filters,
        [id]: updatedFilter,
      };
      return setPreference({ ...preferenceValue, filters: updatedFilters });
    },
    [preferenceValue, setPreference]
  );

  const updateOrgSavedFilter = useCallback(
    ({ id, filters }: { id: string; filters: FilterInput[] }) => {
      const updatedFilter = { ...orgValue.filters[id] };
      updatedFilter.filters = filters;
      const updatedFilters = {
        ...orgValue.filters,
        [id]: updatedFilter,
      };
      return setOrgPreference({ ...orgValue, filters: updatedFilters });
    },
    [orgValue, setOrgPreference]
  );

  const createSavedFilter = useCallback(
    ({
      name,
      filters,
      includeColumns,
    }: {
      name: string;
      filters: FilterInput[];
      includeColumns: boolean;
    }) => {
      const newFilter = newSavedFilter({
        name,
        filters,
        columns: includeColumns ? columnsRef.current : null,
      });
      const updatedFilters = {
        ...preferenceValue.filters,
        [newFilter.id]: newFilter,
      };
      return setPreference({
        ...preferenceValue,
        activeSavedFilterId: newFilter.id,
        filters: updatedFilters,
      });
    },
    [columnsRef, preferenceValue, setPreference]
  );

  const createOrgSavedFilter = useCallback(
    ({
      name,
      filters,
      includeColumns,
    }: {
      name: string;
      filters: FilterInput[];
      includeColumns: boolean;
    }) => {
      const newFilter = newSavedFilter({
        name,
        filters,
        columns: includeColumns ? columnsRef.current : null,
      });
      const updatedFilters = {
        ...orgValue.filters,
        [newFilter.id]: newFilter,
      };
      return setOrgPreference({
        filters: updatedFilters,
      });
    },
    [columnsRef, orgValue.filters, setOrgPreference]
  );

  // rename or change the include columns value
  const editSavedFilter = useCallback(
    ({ id, name, includeColumns }: { id: string; name: string; includeColumns: boolean }) => {
      const renamedFilter = { ...preferenceValue.filters[id] };
      renamedFilter.name = name;
      renamedFilter.columns = includeColumns ? columnsRef.current : null;
      const updatedFilters = {
        ...preferenceValue.filters,
        [id]: renamedFilter,
      };
      return setPreference({ ...preferenceValue, filters: updatedFilters });
    },
    [columnsRef, preferenceValue, setPreference]
  );

  const editOrgSavedFilter = useCallback(
    ({ id, name, includeColumns }: { id: string; name: string; includeColumns: boolean }) => {
      const renamedFilter = { ...orgValue.filters[id] };
      renamedFilter.name = name;
      renamedFilter.columns = includeColumns ? columnsRef.current : null;
      const updatedFilters = {
        ...orgValue.filters,
        [id]: renamedFilter,
      };
      return setOrgPreference({ ...orgValue, filters: updatedFilters });
    },
    [columnsRef, orgValue, setOrgPreference]
  );

  const deleteSavedFilter = useCallback(
    (id: string) => {
      const updatedFilters = { ...preferenceValue.filters };
      delete updatedFilters[id];
      const activeSavedFilterId =
        preferenceValue.activeSavedFilterId === id
          ? undefined
          : preferenceValue.activeSavedFilterId;
      return setPreference({ ...preferenceValue, activeSavedFilterId, filters: updatedFilters });
    },
    [preferenceValue, setPreference]
  );

  const deleteOrgSavedFilter = useCallback(
    (id: string) => {
      const updatedFilters = { ...orgValue.filters };
      delete updatedFilters[id];
      const activeSavedFilterId =
        preferenceValue.activeSavedFilterId === id
          ? undefined
          : preferenceValue.activeSavedFilterId;
      setPreference({ ...preferenceValue, activeSavedFilterId });
      setOrgPreference({ filters: updatedFilters });
    },
    [orgValue.filters, preferenceValue, setOrgPreference, setPreference]
  );

  const setDefaultSavedFilter = useCallback(
    (id: string | undefined) => {
      const updatedFilters = { ...preferenceValue.filters };

      Object.keys(updatedFilters).forEach((key) => {
        const filter = { ...updatedFilters[key] };
        filter.default = key === id;
        updatedFilters[key] = filter;
      });

      const updatedOrgFilters = { ...orgValue.filters };
      Object.keys(updatedOrgFilters).forEach((key) => {
        const filter = { ...updatedOrgFilters[key] };
        filter.default = key === id;
        updatedOrgFilters[key] = filter;
      });

      setPreference({ ...preferenceValue, filters: updatedFilters });
      setOrgPreference({ filters: updatedOrgFilters });
    },
    [orgValue.filters, preferenceValue, setOrgPreference, setPreference]
  );

  const activeSavedFilterId = preferenceValue.activeSavedFilterId;
  const setActiveSavedFilter = useCallback(
    (id: string | undefined) => {
      if (activeSavedFilterId === id) {
        return;
      }

      setPreference({ ...preferenceValue, activeSavedFilterId: id });
      if (!id) {
        return;
      }

      const userPreferenceValue = preferenceValue.filters?.[id];
      const orgPreferenceValue = orgPrefValue.filters?.[id];
      const activePreference = userPreferenceValue ?? orgPreferenceValue;
      const newFilters = activePreference.filters;
      const newColumns = activePreference.columns;

      if (newColumns) {
        setPreferenceColumns(newColumns);
      }

      const [, setValues] = form.values;
      setValues(toForm(newFilters));
      populateForm(newFilters);
    },
    [
      activeSavedFilterId,
      form.values,
      setPreferenceColumns,
      orgPrefValue.filters,
      populateForm,
      preferenceValue,
      setPreference,
      toForm,
    ]
  );

  const defaultSavedFilterId =
    Object.keys(preferenceValue?.filters ?? {}).find((key) => {
      return preferenceValue.filters[key].default;
    }) ??
    Object.keys(orgValue?.filters ?? {}).find((key) => {
      return orgValue.filters[key].default;
    });

  return {
    value: preferenceValue.filters,
    orgValue: orgValue.filters,
    activeSavedFilterId: preferenceValue.activeSavedFilterId,
    activeFilterData: !preferenceValue.activeSavedFilterId
      ? undefined
      : preferenceValue.filters[preferenceValue.activeSavedFilterId] ??
        orgValue.filters[preferenceValue.activeSavedFilterId],
    defaultSavedFilterId,
    loading: loading || orgLoading,
    setDefaultSavedFilter,
    setActiveSavedFilter,

    createSavedFilter,
    editSavedFilter,
    updateSavedFilter,
    deleteSavedFilter,

    createOrgSavedFilter,
    editOrgSavedFilter,
    updateOrgSavedFilter,
    deleteOrgSavedFilter,
    preferenceColumns,

    showFilterChips: preferenceValue.showFilterChips,
    filterChipsHide,
    filterChipsShow,
  };
};
